import React from 'react';
import { DEFAULT_SPRAYFOAM_MAX_THICKNESS_IN } from '../../../../quote-builder/productCoverage';

/**
 * SprayFoamCoverage — Iter18 R2 §1.5.1 slice E.3
 * ---------------------------------------------------------------------------
 * Auto-calculated coverage grid for spray-foam products.
 *
 * Inputs:
 *   • Coverage Per Set (CPS)     — sq ft yield at 1" thickness (single value)
 *   • Max thickness (in)          — grid upper bound; default 14.5, per-item
 *
 * Grid: 0.5"-step rows from 0.5" up to maxThicknessIn. Each row computes
 *   coverage(thickness) = CPS / thickness, rounded to 1 decimal.
 *
 * Layout: 3-column responsive grid (matches legacy LEGACY-QI-2 wire chrome
 * but re-skinned in our red/black design system + .data-table utility).
 *
 * v1 read-only cells — no manual per-thickness override. If a stakeholder
 * needs to correct an outlier, they change CPS and the grid recomputes
 * (IDI-507: the old Calculate button was a no-op and is gone).
 * Source-of-truth: 06-25 stakeholder walkthrough ("we'll click this calculator
 * button, you'll see all of the numbers that have populated…").
 */
export default function SprayFoamCoverage({ coveragePerSet, maxThicknessIn, onChange }) {
  const cps = Number(coveragePerSet) || 0;
  const maxIn = Number(maxThicknessIn) || DEFAULT_SPRAYFOAM_MAX_THICKNESS_IN;

  // Build 0.5"-step ladder from 0.5 → maxIn (inclusive).
  const thicknesses = [];
  for (let t = 0.5; t <= maxIn + 1e-6; t += 0.5) {
    thicknesses.push(Math.round(t * 2) / 2);
  }

  // Legacy layout is 3 columns row-major; we split the ladder into thirds so
  // the visual reading order top-to-bottom → left-to-right matches the wire.
  const columnSize = Math.ceil(thicknesses.length / 3);
  const columns = [
    thicknesses.slice(0, columnSize),
    thicknesses.slice(columnSize, columnSize * 2),
    thicknesses.slice(columnSize * 2),
  ];

  const compute = (t) => (cps > 0 && t > 0 ? (cps / t).toFixed(1) : '—');

  // IDI-507 (9): the no-op Calculate button is gone; the grid recomputes on
  // every change, so there was nothing for it to do.

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <div>
          <label
            className="block text-xs font-medium mb-1"
            style={{ color: 'var(--color-text-default)' }}
            htmlFor="cps-input"
          >
            Coverage Per Set
          </label>
          <div className="flex items-baseline gap-2">
            <input
              id="cps-input"
              type="number"
              min="0"
              step="1"
              value={coveragePerSet ?? ''}
              onChange={(e) =>
                onChange({
                  coverage_per_set:
                    e.target.value === '' ? null : Number(e.target.value),
                })
              }
              className="w-full px-2 py-1.5 rounded border text-sm"
              style={{ borderColor: 'var(--color-border)' }}
              placeholder="e.g. 4000"
            />
            <span className="text-xs whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>
              sq ft @ 1&quot;
            </span>
          </div>
        </div>

        <div>
          <label
            className="block text-xs font-medium mb-1"
            style={{ color: 'var(--color-text-default)' }}
            htmlFor="max-thk-input"
          >
            Max thickness (in)
          </label>
          <input
            id="max-thk-input"
            type="number"
            min="1"
            max="20"
            step="0.5"
            value={maxThicknessIn ?? DEFAULT_SPRAYFOAM_MAX_THICKNESS_IN}
            onChange={(e) =>
              onChange({
                coverage_max_thickness_in:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
            className="w-full px-2 py-1.5 rounded border text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {columns.map((col, ci) => (
          <table
            key={ci}
            className="data-table w-full text-xs"
            style={{ borderCollapse: 'collapse' }}
          >
            <thead>
              <tr>
                <th>Thickness (in)</th>
                <th>Coverage (sq ft)</th>
              </tr>
            </thead>
            <tbody>
              {col.map((t) => (
                <tr key={t}>
                  <td>{t.toFixed(1)}&quot;</td>
                  <td>{compute(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
}
